import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination
} from '@mui/material';
import {
  Search,
  Public,
  Launch,
  TableChart,
  Sync,
  Edit,
  Language,
  Refresh,
  ViewModule,
  TableView,
  Clear,
  Close,
  FilterList,
  Lock,
  LockOpen,
  Shield,
  Description,
  ErrorOutline,
  Warning
} from '@mui/icons-material';
import api from '../services/api';
import DiagnosticPanel from '../components/DiagnosticPanel';

const Domains = () => {
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState(() => localStorage.getItem('domains_searchQuery') || '');
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem('domains_statusFilter') || 'all');
  const [cmsFilter, setCmsFilter] = useState(() => localStorage.getItem('domains_cmsFilter') || 'all');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('domains_viewMode') || 'grid');
  const [loggingIn, setLoggingIn] = useState(null);
  const [checkingPlugin, setCheckingPlugin] = useState({});
  const [updatingPlugin, setUpdatingPlugin] = useState({});
  const [latestPluginVersion, setLatestPluginVersion] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [pluginErrorDialog, setPluginErrorDialog] = useState({
    open: false, domain: null, error: '', diagnostic: null, diagnosing: false,
  });
  const [cfToken, setCfToken] = useState('');
  const [cfFixing, setCfFixing] = useState(false);
  const [showCfInput, setShowCfInput] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => parseInt(localStorage.getItem('domains_rowsPerPage')) || 25);

  useEffect(() => {
    fetchDomains();
    api.get('/admin/plugin/version').then(res => {
      setLatestPluginVersion(res.data.version || null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    applyFilters();
    setPage(0);
    localStorage.setItem('domains_searchQuery', searchQuery);
    localStorage.setItem('domains_statusFilter', statusFilter);
    localStorage.setItem('domains_cmsFilter', cmsFilter);
  }, [domains, searchQuery, statusFilter, cmsFilter]);

  useEffect(() => {
    if (!pluginErrorDialog.open || !pluginErrorDialog.domain) return;
    let cancelled = false;
    setPluginErrorDialog((prev) => ({ ...prev, diagnosing: true, diagnostic: null }));

    api.post(`/sites/${pluginErrorDialog.domain.site_id}/domains/${pluginErrorDialog.domain.id}/diagnose`)
      .then((res) => {
        if (cancelled) return;
        setPluginErrorDialog((prev) => ({ ...prev, diagnostic: res.data, diagnosing: false }));
      })
      .catch(() => {
        if (cancelled) return;
        setPluginErrorDialog((prev) => ({ ...prev, diagnosing: false }));
      });

    return () => { cancelled = true; };
  }, [pluginErrorDialog.open, pluginErrorDialog.domain?.id]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sites/all/domains');
      setDomains(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching domains:', error);
      setError('Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...domains];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.domain_name || '').toLowerCase().includes(q) ||
        (d.hosting_provider || '').toLowerCase().includes(q) ||
        (d.dns_provider || '').toLowerCase().includes(q) ||
        (d.registrar || '').toLowerCase().includes(q) ||
        (d.cms_detected || '').toLowerCase().includes(q) ||
        (d.account_status || '').toLowerCase().includes(q) ||
        (d.account_name || '').toLowerCase().includes(q)
      );
    }

    if (statusFilter === 'active') result = result.filter(d => d.is_active === true);
    else if (statusFilter === 'down') result = result.filter(d => d.is_active === false);
    else if (statusFilter === 'plugin') result = result.filter(d => d.plugin_installed === true);

    if (cmsFilter !== 'all') {
      result = result.filter(d => (d.cms_detected || '').toLowerCase() === cmsFilter.toLowerCase());
    }

    setFilteredDomains(result);
  };

  const getAvailableCMS = () => {
    const cms = [...new Set(domains.map(d => d.cms_detected).filter(Boolean))];
    return cms.sort();
  };

  const handleViewModeChange = (e, newMode) => {
    if (newMode) {
      setViewMode(newMode);
      localStorage.setItem('domains_viewMode', newMode);
    }
  };

  const handleWordPressLogin = async (domain) => {
    const siteUrl = domain.final_url || `https://${domain.domain_name}`;
    setLoggingIn(domain.id);
    try {
      const response = await api.post('/auth/wordpress/sync', {
        site_url: siteUrl,
        email: '',
        display_name: ''
      });
      if (response.data.success && response.data.wordpress_response?.dashboard_url) {
        setSnackbar({ open: true, message: 'WordPress login successful! Redirecting...', severity: 'success' });
        setTimeout(() => {
          window.open(response.data.wordpress_response.dashboard_url, '_blank');
        }, 1000);
      }
    } catch (error) {
      // Handle cases where response.data might be HTML string instead of JSON
      let errorDetail = 'WordPress login failed';
      if (error.response?.data?.detail) {
        errorDetail = error.response.data.detail;
      } else if (typeof error.response?.data === 'string') {
        errorDetail = error.response.data.length > 200
          ? 'WordPress login failed: received unexpected HTML response (possible Cloudflare block or site error)'
          : error.response.data;
      } else if (error.message) {
        errorDetail = error.message;
      }

      const isPluginIssue = errorDetail.includes('rest_forbidden') ||
        errorDetail.includes('Invalid credentials') ||
        errorDetail.includes('rest_no_route') ||
        errorDetail.includes('Cloudflare') ||
        errorDetail.includes('cloudflare') ||
        errorDetail.includes('HTML response') ||
        errorDetail.includes('403') ||
        errorDetail.includes('404') ||
        errorDetail.includes('503') ||
        errorDetail.includes('connect') ||
        errorDetail.includes('timeout') ||
        errorDetail.includes('Network Error') ||
        error.response?.status >= 400;

      if (isPluginIssue) {
        // Mark plugin as failed in the database and log the error
        try {
          await api.post(`/sites/${domain.site_id}/domains/${domain.id}/plugin-failed`, { error: errorDetail });
        } catch (_) { /* best effort */ }

        // Update local state
        setDomains(prev => prev.map(d =>
          d.id === domain.id ? { ...d, plugin_installed: false, plugin_version: null } : d
        ));

        setPluginErrorDialog({
          open: true,
          domain: domain,
          error: errorDetail
        });
      } else {
        setSnackbar({ open: true, message: errorDetail, severity: 'error' });
      }
    } finally {
      setLoggingIn(null);
    }
  };

  const handleCheckPlugin = async (domain) => {
    setCheckingPlugin(prev => ({ ...prev, [domain.id]: true }));
    try {
      const response = await api.post(`/sites/${domain.site_id}/domains/${domain.id}/scan`);
      const updated = response.data;
      setDomains(prev => prev.map(d => d.id === domain.id ? { ...d, ...updated } : d));

      let message, severity;
      if (updated.plugin_installed && updated.plugin_auto_configured) {
        message = `Plugin found on ${domain.domain_name} (${updated.plugin_version}) — initial handshake completed, credentials configured`;
        severity = 'success';
      } else if (updated.plugin_installed) {
        message = `Plugin found on ${domain.domain_name} (${updated.plugin_version || 'unknown version'})`;
        severity = 'success';
      } else {
        message = `No plugin found on ${domain.domain_name}`;
        severity = 'warning';
      }
      setSnackbar({ open: true, message, severity });
    } catch (error) {
      setSnackbar({ open: true, message: `Scan failed for ${domain.domain_name}`, severity: 'error' });
    } finally {
      setCheckingPlugin(prev => ({ ...prev, [domain.id]: false }));
    }
  };

  const handleUpdatePlugin = async (domain) => {
    setUpdatingPlugin(prev => ({ ...prev, [domain.id]: 'downloading' }));
    setSnackbar({
      open: true,
      message: `Updating plugin on ${domain.domain_name} — downloading package...`,
      severity: 'info'
    });
    try {
      setUpdatingPlugin(prev => ({ ...prev, [domain.id]: 'installing' }));
      const response = await api.post(`/admin/plugin/update-domain/${domain.id}`);
      const result = response.data;
      if (result.success) {
        setDomains(prev => prev.map(d =>
          d.id === domain.id ? { ...d, plugin_version: result.new_version } : d
        ));
        setSnackbar({
          open: true,
          message: `Plugin updated on ${domain.domain_name}: ${result.previous_version} → ${result.new_version}`,
          severity: 'success'
        });
      } else {
        setSnackbar({ open: true, message: `Update failed: ${result.error}`, severity: 'error' });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Update failed for ${domain.domain_name}: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
    } finally {
      setUpdatingPlugin(prev => ({ ...prev, [domain.id]: false }));
    }
  };

  const isPluginOutdated = (domain) => {
    if (!latestPluginVersion || !domain.plugin_version) return true;
    const normalize = (v) => (v || '').replace(/^v/i, '').trim();
    return normalize(domain.plugin_version) !== normalize(latestPluginVersion);
  };

  const getStatusColor = (domain) => {
    if (domain.is_active === true) return { border: 'success.main', bg: 'rgba(76, 175, 80, 0.02)', shadow: '0 2px 8px rgba(76, 175, 80, 0.08)' };
    if (domain.is_active === false) return { border: 'error.main', bg: 'rgba(244, 67, 54, 0.02)', shadow: '0 2px 8px rgba(244, 67, 54, 0.08)' };
    return { border: 'grey.300', bg: 'transparent', shadow: 'none' };
  };

  const renderTableView = () => (
    <>
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1000 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
            <Tooltip title="Account status from NERV" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>Account</TableCell></Tooltip>
            <Tooltip title="Whether the site responds to HTTP requests" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>Status</TableCell></Tooltip>
            <Tooltip title="SSL/TLS certificate status and expiry" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>SSL</TableCell></Tooltip>
            <Tooltip title="Content Management System detected" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>CMS</TableCell></Tooltip>
            <Tooltip title="NKP Control Panel WordPress plugin" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>Plugin</TableCell></Tooltip>
            <Tooltip title="MainWP connection status" arrow>
              <TableCell sx={{ fontWeight: 600, cursor: 'help' }}>MainWP</TableCell>
            </Tooltip>
            <Tooltip title="Cloudflare DNS/CDN protection" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>CF</TableCell></Tooltip>
            <Tooltip title="Hosting provider from reverse DNS" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>Hosting</TableCell></Tooltip>
            <Tooltip title="Domain registrar from WHOIS" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>Registrar</TableCell></Tooltip>
            <Tooltip title="Domain registration expiry date" arrow><TableCell sx={{ fontWeight: 600, cursor: 'help' }}>Expires</TableCell></Tooltip>
            <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredDomains.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((domain) => (
            <TableRow key={domain.id} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => navigate(`/domains/${domain.id}`)}
                  >
                    {domain.domain_name}
                  </Typography>
                  {domain.is_primary && <Chip label="Main" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
                </Box>
              </TableCell>
              <TableCell>
                {domain.account_status ? (
                  <Tooltip title={`Account: ${domain.account_name || 'Unknown'}`} arrow>
                    <Chip label={domain.account_status} size="small"
                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600,
                        bgcolor: domain.account_status === 'Active' ? 'success.light' : domain.account_status === 'Cancelled' ? 'error.light' : 'grey.200',
                        color: domain.account_status === 'Active' ? 'success.dark' : domain.account_status === 'Cancelled' ? 'error.dark' : 'text.secondary',
                      }} />
                  </Tooltip>
                ) : <Typography variant="body2" color="text.secondary">—</Typography>}
              </TableCell>
              <TableCell>
                <Tooltip title={domain.is_active === null ? 'Not scanned yet' : domain.is_active ? 'Site is responding to HTTP requests' : 'Site is not responding or returning errors'} arrow>
                  <span>{domain.is_active === null ? <Chip label="?" size="small" /> :
                    <Chip label={domain.is_active ? 'Up' : 'Down'} color={domain.is_active ? 'success' : 'error'} size="small" />}</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                {domain.ssl_status ? (
                  <Tooltip title={domain.ssl_status === 'valid' ? `SSL certificate is valid${domain.ssl_expiry_date ? `, expires ${domain.ssl_expiry_date}` : ''}` : domain.ssl_status === 'expired' ? 'SSL certificate has expired — visitors will see a security warning' : domain.ssl_status === 'missing' ? 'No SSL certificate found — site is not using HTTPS' : 'SSL check encountered an error'} arrow>
                    <Chip label={domain.ssl_status} color={domain.ssl_status === 'valid' ? 'success' : domain.ssl_status === 'expired' ? 'error' : 'default'} size="small" />
                  </Tooltip>
                ) : '—'}
              </TableCell>
              <TableCell>
                <Tooltip title={domain.cms_detected ? `Detected CMS: ${domain.cms_detected}` : 'No CMS detected or not scanned'} arrow>
                  <span>{domain.cms_detected || '—'}</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                {domain.plugin_installed === null ? (
                  <Tooltip title="Plugin status unknown — not scanned or CMS is not WordPress" arrow><span>—</span></Tooltip>
                ) : (
                  <Tooltip title={domain.plugin_installed ? `NKP Control Panel plugin installed${domain.plugin_version ? ` (${domain.plugin_version})` : ''}` : 'NKP Control Panel plugin is not installed'} arrow>
                    <Chip label={domain.plugin_installed ? '✓' : 'No'} color={domain.plugin_installed ? 'success' : 'default'} size="small" />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                {domain.mainwp_id ? (
                  <Chip label="✓" color="success" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                ) : (
                  <Chip label="—" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
              </TableCell>
              <TableCell>
                {domain.cloudflare_status === null ? (
                  <Tooltip title="Cloudflare status not scanned" arrow><span>—</span></Tooltip>
                ) : (
                  <Tooltip title={domain.cloudflare_status ? 'Domain is using Cloudflare DNS/CDN' : 'Domain is not using Cloudflare'} arrow>
                    <Chip label={domain.cloudflare_status ? 'CF' : 'No'} color={domain.cloudflare_status ? 'warning' : 'default'} size="small" />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                <Tooltip title={domain.hosting_provider ? `Hosting provider detected from reverse DNS` : 'Hosting provider not detected'} arrow>
                  <span>{domain.hosting_provider || '—'}</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={domain.registrar ? `Domain registrar from WHOIS lookup` : 'Registrar not detected'} arrow>
                  <span>{domain.registrar || '—'}</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={domain.domain_expiry_date ? `Domain registration expires on ${domain.domain_expiry_date}` : 'Expiry date not available'} arrow>
                  <span>{domain.domain_expiry_date || '—'}</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                  {domain.plugin_installed ? (
                    <>
                      <Button size="small" variant="contained" color="success"
                        startIcon={loggingIn === domain.id ? <CircularProgress size={14} color="inherit" /> : <Sync />}
                        onClick={() => handleWordPressLogin(domain)}
                        disabled={loggingIn === domain.id}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 'auto' }}
                      >
                        Login
                      </Button>
                      {isPluginOutdated(domain) && (
                        <Tooltip title={updatingPlugin[domain.id] ?
                          (updatingPlugin[domain.id] === 'downloading' ? 'Downloading plugin package...' : 'Installing update...') :
                          `Update plugin from ${domain.plugin_version || '?'} to ${latestPluginVersion || 'latest'}`
                        } arrow>
                          <Button size="small" variant="outlined"
                            color={updatingPlugin[domain.id] ? 'warning' : 'primary'}
                            startIcon={updatingPlugin[domain.id] ? <CircularProgress size={14} /> : <Refresh />}
                            onClick={() => handleUpdatePlugin(domain)}
                            disabled={!!updatingPlugin[domain.id]}
                            sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 'auto' }}
                          >
                            {updatingPlugin[domain.id] === 'downloading' ? 'Downloading...' :
                             updatingPlugin[domain.id] === 'installing' ? 'Installing...' : 'Update'}
                          </Button>
                        </Tooltip>
                      )}
                      <Tooltip title="View form submissions" arrow>
                        <IconButton size="small" onClick={() => domain.site_id && navigate(`/forms/${domain.site_id}`)} sx={{ color: 'info.main', p: 0.25 }}>
                          <Description sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="Scan domain to check plugin status" arrow>
                      <Button size="small" variant="outlined" color="warning"
                        startIcon={checkingPlugin[domain.id] ? <CircularProgress size={14} /> : <Refresh />}
                        onClick={() => handleCheckPlugin(domain)}
                        disabled={checkingPlugin[domain.id]}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 'auto' }}
                      >
                        Check
                      </Button>
                    </Tooltip>
                  )}
                  {domain.google_sheet_url && (
                    <Tooltip title="Open Google Sheet" arrow>
                      <IconButton size="small" onClick={() => window.open(domain.google_sheet_url, '_blank')} sx={{ color: 'success.main', p: 0.25 }}>
                        <TableChart sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton size="small" onClick={() => window.open(domain.final_url || `https://${domain.domain_name}`, '_blank')} sx={{ color: 'text.secondary', p: 0.25 }} title="Visit">
                    <Launch sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => navigate(`/domains/${domain.id}`)} sx={{ color: 'primary.main', p: 0.25 }} title="Edit Domain">
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <TablePagination
      component="div"
      count={filteredDomains.length}
      page={page}
      onPageChange={(e, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); localStorage.setItem('domains_rowsPerPage', e.target.value); }}
      rowsPerPageOptions={[25, 50, 100]}
    />
    </>
  );

  if (loading) {
    return (
      <Container><Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box></Container>
    );
  }

  return (
    <>
      <Box sx={{ py: 4 }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Box>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                  Domains
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                  Manage all domains across your accounts
                </Typography>
              </Box>
            </Box>

            {/* Search and Filters */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                <TextField
                  variant="outlined"
                  placeholder="Search domains, hosting, registrar, CMS..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ minWidth: 300, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary' }} /></InputAdornment>,
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setSearchQuery('')} size="small"><Clear /></IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl variant="outlined" sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status" sx={{ bgcolor: 'background.paper' }}>
                    <MenuItem value="all">All Domains</MenuItem>
                    <MenuItem value="active">Active (Up)</MenuItem>
                    <MenuItem value="down">Down</MenuItem>
                    <MenuItem value="plugin">Plugin Installed</MenuItem>
                  </Select>
                </FormControl>
                <FormControl variant="outlined" sx={{ minWidth: 160 }}>
                  <InputLabel>CMS</InputLabel>
                  <Select value={cmsFilter} onChange={(e) => setCmsFilter(e.target.value)} label="CMS" sx={{ bgcolor: 'background.paper' }}>
                    <MenuItem value="all">All CMS</MenuItem>
                    {getAvailableCMS().map(cms => <MenuItem key={cms} value={cms}>{cms}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                {(searchQuery || statusFilter !== 'all' || cmsFilter !== 'all') && (
                  <IconButton onClick={() => { setSearchQuery(''); setStatusFilter('all'); setCmsFilter('all'); }}
                    sx={{ color: 'error.main', backgroundColor: 'error.light', borderRadius: 2, p: 1.5, '&:hover': { backgroundColor: 'error.main', color: 'white' } }}
                    title="Clear All Filters"
                  ><Close /></IconButton>
                )}
                <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small"
                  sx={{ '& .MuiToggleButton-root': { borderRadius: 0, px: 1.5, py: 0.75, minWidth: 'auto', fontSize: '0.875rem', '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } } } }}
                >
                  <ToggleButton value="grid"><ViewModule sx={{ mr: 0.5, fontSize: 16 }} />Card</ToggleButton>
                  <ToggleButton value="table"><TableView sx={{ mr: 0.5, fontSize: 16 }} />Table</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {/* Results Counter */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {Math.min(page * rowsPerPage + 1, filteredDomains.length)}–{Math.min((page + 1) * rowsPerPage, filteredDomains.length)} of {filteredDomains.length} domains{filteredDomains.length !== domains.length ? ` (${domains.length} total)` : ''}
              </Typography>
            </Box>

            {/* Active Filters */}
            {(searchQuery || statusFilter !== 'all' || cmsFilter !== 'all') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FilterList sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Active Filters:</Typography>
                </Box>
                {searchQuery && <Chip label={`Search: "${searchQuery}"`} onDelete={() => setSearchQuery('')} size="small" color="primary" variant="outlined" />}
                {statusFilter !== 'all' && <Chip label={`Status: ${statusFilter}`} onDelete={() => setStatusFilter('all')} size="small" color="secondary" variant="outlined" />}
                {cmsFilter !== 'all' && <Chip label={`CMS: ${cmsFilter}`} onDelete={() => setCmsFilter('all')} size="small" color="info" variant="outlined" />}
              </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Content */}
            {viewMode === 'table' ? renderTableView() : (
              <>
              <Grid container spacing={3}>
                {filteredDomains.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((domain) => {
                  const colors = getStatusColor(domain);
                  return (
                    <Grid item xs={12} sm={6} md={6} lg={4} key={domain.id}>
                      <Card
                        elevation={0}
                        sx={{
                          border: '1px solid',
                          borderColor: 'grey.200',
                          borderRadius: 3,
                          transition: 'all 0.3s ease',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          bgcolor: colors.bg,
                          boxShadow: colors.shadow,
                          '&:hover': { borderColor: 'grey.300', transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }
                        }}
                      >
                        <CardContent sx={{ pb: 1, flexGrow: 1 }}>
                          {/* Domain name + Main badge */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                            <Public sx={{ color: 'primary.main', mr: 1.5, mt: 0.5, fontSize: 24, flexShrink: 0 }} />
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="h6" component="h2"
                                sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                title={domain.domain_name}
                                onClick={() => navigate(`/domains/${domain.id}`)}
                              >
                                {domain.domain_name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3, fontSize: '0.8rem' }}
                                title={domain.final_url}
                              >
                                {domain.final_url || `https://${domain.domain_name}`}
                              </Typography>
                            </Box>
                            {domain.is_primary && <Chip label="Main" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, ml: 1 }} />}
                          </Box>

                          {/* Info box */}
                          <Box sx={{ bgcolor: 'action.hover', px: 2, py: 1.5, borderRadius: 2 }}>
                            {/* Status + SSL row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title={domain.is_active === null ? 'Not scanned yet' : domain.is_active ? 'Site is responding to HTTP requests' : 'Site is not responding or returning errors'} arrow>
                                  <span>{domain.is_active === null ? <Chip label="Unknown" size="small" /> :
                                    <Chip label={domain.is_active ? 'Active' : 'Down'} color={domain.is_active ? 'success' : 'error'} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />}</span>
                                </Tooltip>
                                {domain.ssl_status && (
                                  <Tooltip title={domain.ssl_status === 'valid' ? `SSL certificate valid${domain.ssl_expiry_date ? `, expires ${domain.ssl_expiry_date}` : ''}` : domain.ssl_status === 'expired' ? 'SSL certificate has expired' : 'SSL certificate missing or error'} arrow>
                                    <Chip
                                      icon={domain.ssl_status === 'valid' ? <Lock sx={{ fontSize: 12 }} /> : <LockOpen sx={{ fontSize: 12 }} />}
                                      label={domain.ssl_status}
                                      color={domain.ssl_status === 'valid' ? 'success' : domain.ssl_status === 'expired' ? 'error' : 'default'}
                                      size="small" variant="outlined"
                                      sx={{ height: 20, fontSize: '0.6rem' }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {domain.account_status && (
                                  <Tooltip title={`Account: ${domain.account_name || 'Unknown'}`} arrow>
                                    <Chip label={domain.account_status} size="small"
                                      sx={{ height: 20, fontSize: '0.6rem', fontWeight: 600,
                                        bgcolor: domain.account_status === 'Active' ? 'success.light' : domain.account_status === 'Cancelled' ? 'error.light' : 'grey.200',
                                        color: domain.account_status === 'Active' ? 'success.dark' : domain.account_status === 'Cancelled' ? 'error.dark' : 'text.secondary',
                                      }} />
                                  </Tooltip>
                                )}
                                {domain.cloudflare_status && (
                                  <Tooltip title="Domain is using Cloudflare DNS/CDN" arrow>
                                    <Chip icon={<Shield sx={{ fontSize: 12 }} />} label="CF" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.6rem' }} />
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>

                            {/* CMS + Plugin row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: domain.hosting_provider || domain.registrar ? 1 : 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Tooltip title={domain.cms_detected ? `Detected CMS: ${domain.cms_detected}` : 'No CMS detected or not scanned'} arrow>
                                  <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                                    {domain.cms_detected || 'Unknown CMS'}
                                  </Typography>
                                </Tooltip>
                                {domain.plugin_installed && (
                                  <Tooltip title={`NKP Control Panel plugin installed${domain.plugin_version ? ` (${domain.plugin_version})` : ''}`} arrow>
                                    <Chip label={`Plugin ${domain.plugin_version || '✓'}`} size="small" color="success" sx={{ height: 16, fontSize: '0.55rem', ml: 1 }} />
                                  </Tooltip>
                                )}
                                {domain.mainwp_id && (
                                  <Chip label="MainWP" size="small" color="info" sx={{ height: 16, fontSize: '0.55rem', ml: 0.5 }} />
                                )}
                              </Box>
                              {domain.domain_expiry_date && (
                                <Tooltip title={`Domain registration expires on ${domain.domain_expiry_date}`} arrow>
                                  <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                                    Expires: {domain.domain_expiry_date}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>

                            {/* Hosting + Registrar row */}
                            {(domain.hosting_provider || domain.registrar) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {domain.hosting_provider && (
                                  <Tooltip title="Hosting provider detected from reverse DNS lookup" arrow>
                                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                                      Host: {domain.hosting_provider}
                                    </Typography>
                                  </Tooltip>
                                )}
                                {domain.registrar && (
                                  <Tooltip title="Domain registrar from WHOIS lookup" arrow>
                                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                                      Reg: {domain.registrar}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Box>
                            )}
                          </Box>
                        </CardContent>

                        <CardActions sx={{ px: 2, pb: 2 }}>
                          {domain.plugin_installed ? (
                            <>
                              <Button size="small" variant="contained" color="success"
                                startIcon={loggingIn === domain.id ? <CircularProgress size={14} color="inherit" /> : <Sync />}
                                onClick={() => handleWordPressLogin(domain)}
                                disabled={loggingIn === domain.id}
                                sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.8rem' }}
                              >
                                WP Login
                              </Button>
                              {isPluginOutdated(domain) && (
                                <Tooltip title={updatingPlugin[domain.id] ?
                                  (updatingPlugin[domain.id] === 'downloading' ? 'Downloading plugin package...' : 'Installing update...') :
                                  `Update plugin from ${domain.plugin_version || '?'} to ${latestPluginVersion || 'latest'}`
                                } arrow>
                                  <Button size="small" variant="outlined"
                                    color={updatingPlugin[domain.id] ? 'warning' : 'primary'}
                                    startIcon={updatingPlugin[domain.id] ? <CircularProgress size={14} /> : <Refresh />}
                                    onClick={() => handleUpdatePlugin(domain)}
                                    disabled={!!updatingPlugin[domain.id]}
                                    sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.8rem' }}
                                  >
                                    {updatingPlugin[domain.id] === 'downloading' ? 'Downloading...' :
                                     updatingPlugin[domain.id] === 'installing' ? 'Installing...' : 'Update'}
                                  </Button>
                                </Tooltip>
                              )}
                              <Tooltip title="View form submissions" arrow>
                                <Button size="small" variant="outlined" color="info"
                                  startIcon={<Description />}
                                  onClick={() => domain.site_id && navigate(`/forms/${domain.site_id}`)}
                                  sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.8rem' }}
                                >
                                  Forms
                                </Button>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Scan domain to check plugin status" arrow>
                              <Button size="small" variant="outlined" color="warning"
                                startIcon={checkingPlugin[domain.id] ? <CircularProgress size={14} /> : <Refresh />}
                                onClick={() => handleCheckPlugin(domain)}
                                disabled={checkingPlugin[domain.id]}
                                sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.8rem' }}
                              >
                                Check Plugin
                              </Button>
                            </Tooltip>
                          )}
                          {domain.google_sheet_url && (
                            <Tooltip title="Open Google Sheet" arrow>
                              <IconButton size="small" onClick={() => window.open(domain.google_sheet_url, '_blank')} sx={{ color: 'success.main' }}>
                                <TableChart />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Box sx={{ flexGrow: 1 }} />
                          <IconButton size="small" onClick={() => navigate(`/domains/${domain.id}`)} sx={{ color: 'primary.main' }} title="Edit Domain">
                            <Edit />
                          </IconButton>
                          <IconButton size="small" onClick={() => window.open(domain.final_url || `https://${domain.domain_name}`, '_blank')} sx={{ color: 'text.secondary' }} title="Visit Site">
                            <Launch />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              <TablePagination
                component="div"
                count={filteredDomains.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); localStorage.setItem('domains_rowsPerPage', e.target.value); }}
                rowsPerPageOptions={[25, 50, 100]}
              />
              </>
            )}
          </Box>
        </Container>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Plugin Error Dialog */}
      <Dialog
        open={pluginErrorDialog.open}
        onClose={() => { setPluginErrorDialog({ open: false, domain: null, error: '', diagnostic: null, diagnosing: false }); setShowCfInput(false); setCfToken(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Warning sx={{ color: 'warning.main', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            WordPress Connection Issue
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Unable to connect to <strong>{pluginErrorDialog.domain?.domain_name}</strong>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The WordPress login failed, which usually means one of the following:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 1 } }}>
            <Typography component="li" variant="body2" color="text.secondary">
              The <strong>NKP Control Panel plugin</strong> has been deactivated or uninstalled
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              The plugin credentials have been changed or reset
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              The site is experiencing downtime or connectivity issues
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              WordPress REST API has been restricted by a security plugin
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>Cloudflare bot protection</strong> is blocking the API request
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <DiagnosticPanel
              result={pluginErrorDialog.diagnostic}
              loading={pluginErrorDialog.diagnosing}
            />
          </Box>

          {/* Cloudflare Fix Section */}
          {pluginErrorDialog.diagnostic?.classification === 'cloudflare_blocking' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Cloudflare detected — you can fix this automatically
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Provide a Cloudflare API token to whitelist the NERV server IP ({`35.208.152.255`}) on this domain's WAF rules.
              </Typography>
              <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5, mb: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>How to get a Cloudflare API Token:</Typography>
                <Box component="ol" sx={{ pl: 2, m: 0, '& li': { mb: 0.25 } }}>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Go to <strong>dash.cloudflare.com</strong> and log in
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Click your profile icon (top right) → <strong>My Profile</strong>
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Select <strong>API Tokens</strong> tab → <strong>Create Token</strong>
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Use <strong>"Create Custom Token"</strong> template
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Add permission: <strong>Zone → Firewall Services → Edit</strong>
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Zone Resources: <strong>Include → Specific zone</strong> → select the domain
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Click <strong>Continue to summary → Create Token</strong>
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Copy the token and paste it below
                  </Typography>
                </Box>
              </Box>
              {!showCfInput ? (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="info"
                    startIcon={<Shield />}
                    onClick={() => {
                      setShowCfInput(true);
                      setCfToken(pluginErrorDialog.domain?.cloudflare_api_token_masked ? '' : '');
                    }}
                  >
                    {pluginErrorDialog.domain?.cloudflare_api_token_masked ? 'Fix with Saved Token' : 'Fix with Cloudflare API'}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mt: 1.5 }}>
                  {!pluginErrorDialog.domain?.cloudflare_api_token_masked && (
                    <TextField
                      size="small"
                      fullWidth
                      label="Cloudflare API Token"
                      value={cfToken}
                      onChange={(e) => setCfToken(e.target.value)}
                      placeholder="Paste your Cloudflare API token"
                      type="password"
                      sx={{ mb: 1 }}
                      helperText="Token needs Zone.Firewall Services permission"
                    />
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    color="info"
                    startIcon={cfFixing ? <CircularProgress size={14} color="inherit" /> : <Shield />}
                    disabled={cfFixing || (!pluginErrorDialog.domain?.cloudflare_api_token_masked && !cfToken.trim())}
                    onClick={async () => {
                      setCfFixing(true);
                      try {
                        const payload = cfToken.trim() ? { cloudflare_api_token: cfToken.trim() } : {};
                        const res = await api.post(
                          `/sites/${pluginErrorDialog.domain.site_id}/domains/${pluginErrorDialog.domain.id}/cloudflare-whitelist`,
                          payload
                        );
                        setSnackbar({ open: true, message: res.data.message, severity: 'success' });
                        setShowCfInput(false);
                        setCfToken('');
                        // Retry login after a short delay
                        setTimeout(() => {
                          setPluginErrorDialog({ open: false, domain: null, error: '', diagnostic: null, diagnosing: false });
                          handleCheckPlugin(pluginErrorDialog.domain);
                        }, 2000);
                      } catch (err) {
                        const msg = err.response?.data?.detail || 'Cloudflare API error';
                        setSnackbar({ open: true, message: msg, severity: 'error' });
                      } finally {
                        setCfFixing(false);
                      }
                    }}
                  >
                    {cfFixing ? 'Whitelisting...' : 'Whitelist Server IP'}
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          <Alert severity="info" icon={<ErrorOutline />} sx={{ mb: 1 }}>
            Please contact your administrator if the issue persists after trying the fixes above.
          </Alert>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {pluginErrorDialog.error?.length > 300 ? pluginErrorDialog.error.substring(0, 300) + '...' : pluginErrorDialog.error}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setPluginErrorDialog({ open: false, domain: null, error: '', diagnostic: null, diagnosing: false });
              setShowCfInput(false);
              setCfToken('');
              if (pluginErrorDialog.domain) handleCheckPlugin(pluginErrorDialog.domain);
            }}
            startIcon={<Refresh />}
          >
            Re-scan Domain
          </Button>
          <Button
            variant="contained"
            onClick={() => { setPluginErrorDialog({ open: false, domain: null, error: '', diagnostic: null, diagnosing: false }); setShowCfInput(false); setCfToken(''); }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Domains;
